import { isAxiosError } from 'axios'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import { describeError } from '@/lib/errors'
import { settingsLabel } from '@/lib/settingsRoutes'

/**
 * Billing helpers shared by the invoice detail, record-payment, send and
 * Settings screens.
 *
 * Who may issue: mirrors backend/src/lib/billingPermissions.ts (the roles that
 * may send invoices: owner/admin and sales). The server enforces it; this only
 * decides whether to offer the button.
 */
export function canIssueInvoices(role: string | undefined | null): boolean {
  return role === 'owner' || role === 'admin' || role === 'sales'
}

export function useCanIssueInvoices(): boolean {
  return canIssueInvoices(useAuthStore((s) => s.user?.role))
}

export interface ApiErrorInfo {
  status?: number
  code?: string
  message: string
  data: Record<string, unknown>
}

/**
 * Status, machine code and the server's own sentence for a failed request.
 * Handlers use `error` (sometimes a code like `email_not_configured` with the
 * sentence in `message`); Fastify validation uses `message`.
 */
export function apiErrorInfo(err: unknown, fallback: string): ApiErrorInfo {
  const res = (err as { response?: { status?: number; data?: unknown } } | null)?.response
  if (!isAxiosError(err) && !res) return { message: fallback, data: {} }
  if (!res) return { message: 'Server not reachable. Check your connection and try again.', data: {} }
  const data = (res.data ?? {}) as Record<string, unknown>
  const error = typeof data.error === 'string' ? data.error : undefined
  const message = typeof data.message === 'string' ? data.message : undefined
  const looksLikeCode = (s?: string) => !!s && /^[a-z0-9_]+$/.test(s)
  const code = typeof data.code === 'string' ? data.code : looksLikeCode(error) ? error : undefined
  const sentence = (looksLikeCode(error) ? message : error ?? message) ?? fallback
  return { status: res.status, code, message: sentence, data }
}

/** Plain-language text for the billing error codes the API returns. */
const BILLING_COPY: Record<string, string> = {
  INVOICE_NOT_ISSUED: 'This invoice is still a draft. Send it (or mark it as sent) before taking a payment.',
  INVOICE_ISSUE_FORBIDDEN: 'Your role can’t send invoices. Ask an admin or office user to send it.',
  INVOICE_NEEDS_CUSTOMER: 'This invoice has no customer. Edit it and pick a customer first.',
  INVOICE_ZERO_TOTAL: 'This invoice totals $0.00. Add a line item before sending it.',
  INVOICE_ALREADY_ISSUED: 'This invoice was already sent. Showing its latest status.',
  INVOICE_CLOSED: 'This invoice is closed and can’t take payments.',
  INVOICE_NEEDS_DEAL: `This older invoice isn’t linked to an opportunity. An admin can link it in ${settingsLabel('billingTools')}.`,
  email_not_configured: 'Email isn’t set up yet, so nothing was sent. Mark the invoice as sent instead and share it another way.',
  email_delivery_failed: 'The email didn’t go through, so the invoice was not sent. Try again in a moment.',
}

/**
 * Plain words for a failed billing request: the coded billing copy above,
 * else F2's describeError (which only passes a short, human server sentence
 * through), else the caller's fallback. Never a raw server string.
 */
export function billingErrorMessage(err: unknown, fallback: string): string {
  const info = apiErrorInfo(err, fallback)
  if (info.code && BILLING_COPY[info.code]) return BILLING_COPY[info.code]
  // Anything without an HTTP response (a thrown Error, a bug) gets the caller's words.
  const res = (err as { response?: { status?: number } } | null)?.response
  if (!isAxiosError(err) && !res) return fallback
  if (!res) return info.message
  // Judge the server's human sentence (not a machine code) with F2's filter.
  return describeError({ response: { status: info.status, data: { error: info.message } } }).message
}

export interface StripeStatus {
  configured: boolean
  usable: boolean
  mode: 'test' | 'live' | null
  webhookConfigured: boolean
  problem?: string
}

/** Server-level Stripe state (keys live in the server environment, not Settings). */
export function useStripeStatus(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['payments', 'stripe-status'],
    queryFn: async () => (await apiClient.get('/api/payments/stripe-status')).data.data as StripeStatus,
    staleTime: 5 * 60_000,
    retry: false,
    enabled: opts.enabled ?? true,
  })
}

/** Issue a DRAFT invoice (locks totals, links its deal). */
export async function issueInvoice(invoiceId: string): Promise<void> {
  await apiClient.post(`/api/payments/invoices/${invoiceId}/issue`, {})
}
